<script setup lang="ts">
import { reactive, ref } from "vue";
import Drawer from "primevue/drawer";
import Button from "primevue/button";
import InputText from "primevue/inputtext";
import Textarea from "primevue/textarea";
import Select from "primevue/select";
import Message from "primevue/message";
import { useToast } from "primevue/usetoast";

import { createEvent, type CreateEventPayload, type Severity } from "../api";

defineProps<{
  visible: boolean;
}>();

const emit = defineEmits<{
  (e: "update:visible", v: boolean): void;
  (e: "created"): void;
}>();

const toast = useToast();

interface Draft {
  category: "process_deviation" | "technical_quality_issue";
  severity: Exclude<Severity, "none">;
  owner: string;
  summary: string;
  description: string;
  contributors: string; // one email per line in the textarea
  policyDocument: string;
  policySection: string;
}

const draft = reactive<Draft>(blank());
const error = ref<string | null>(null);
const submitting = ref(false);

function blank(): Draft {
  return {
    category: "process_deviation",
    severity: "medium",
    owner: "",
    summary: "",
    description: "",
    contributors: "",
    policyDocument: "",
    policySection: "",
  };
}

// The draft survives drawer-close on purpose — a misclick on the backdrop
// shouldn't lose a half-written event. `resetForm()` runs only on successful
// submit; closing without submitting leaves the draft intact for next open.

function resetForm(): void {
  const fresh = blank();
  for (const k of Object.keys(draft) as (keyof Draft)[]) {
    (draft as Record<string, unknown>)[k] = fresh[k];
  }
  error.value = null;
}

function parseContributors(): { contributor: string }[] {
  return draft.contributors
    .split(/[\n,]+/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0)
    .map((c) => ({ contributor: c }));
}

const categoryOptions = [
  { label: "Process deviation", value: "process_deviation" },
  { label: "Technical quality issue", value: "technical_quality_issue" },
];
const severityOptions = [
  { label: "low", value: "low" },
  { label: "medium", value: "medium" },
  { label: "high", value: "high" },
  { label: "critical", value: "critical" },
];

async function onSubmit(): Promise<void> {
  error.value = null;

  // Client-side validation that mirrors the backend's rules so the user gets
  // immediate feedback rather than a round-trip + 422.
  if (!draft.summary.trim()) {
    error.value = "A one-line summary is required.";
    return;
  }
  if (draft.summary.includes("\n") || draft.summary.includes("\r")) {
    error.value = "The summary must be a single line.";
    return;
  }
  if (!draft.owner.trim()) {
    error.value = "Owner is required.";
    return;
  }
  if (!draft.description.trim()) {
    error.value = "A description is required.";
    return;
  }

  const payload: CreateEventPayload = {
    date: new Date().toISOString(),
    category: draft.category,
    severity: draft.severity,
    owner: draft.owner.trim(),
    summary: draft.summary.trim(),
    description: draft.description,
    contributors: parseContributors(),
    review_id: null,
    policy_document: draft.policyDocument.trim() || null,
    policy_section: draft.policySection.trim() || null,
  };

  submitting.value = true;
  try {
    await createEvent(payload);
    toast.add({
      severity: "success",
      summary: "Event submitted",
      detail: "Owner and contributors have been notified.",
      life: 3000,
    });
    resetForm();
    emit("created");
    emit("update:visible", false);
  } catch (e) {
    error.value = String(e);
  } finally {
    submitting.value = false;
  }
}

function onVisibleUpdate(v: boolean): void {
  emit("update:visible", v);
}
</script>

<template>
  <Drawer
    :visible="visible"
    @update:visible="onVisibleUpdate"
    position="right"
    :style="{ width: 'min(640px, 95vw)' }"
  >
    <template #header>
      <div class="drawer-header">
        <div class="drawer-title">Submit a quality event</div>
        <div class="drawer-sub">
          Anyone can file an event. You'll be recorded as the reporter.
        </div>
      </div>
    </template>

    <form class="form" data-testid="event-create-form" @submit.prevent="onSubmit">
      <div class="row">
        <div class="field">
          <label for="cat">Category</label>
          <Select
            id="cat"
            v-model="draft.category"
            :options="categoryOptions"
            optionLabel="label"
            optionValue="value"
          />
        </div>
        <div class="field">
          <label for="sev">Severity</label>
          <Select
            id="sev"
            v-model="draft.severity"
            :options="severityOptions"
            optionLabel="label"
            optionValue="value"
          />
        </div>
      </div>

      <div class="field">
        <label for="summary">Summary (one line)</label>
        <InputText
          id="summary"
          v-model="draft.summary"
          placeholder="e.g. Missing test coverage on new billing path"
          :maxlength="255"
        />
      </div>

      <div class="field">
        <label for="owner">Owner email</label>
        <InputText
          id="owner"
          v-model="draft.owner"
          placeholder="josiah@multiply.cloud"
        />
      </div>

      <div class="field">
        <label for="desc">Description</label>
        <Textarea
          id="desc"
          v-model="draft.description"
          rows="6"
          autoResize
          placeholder="Markdown supported. Include evidence inline."
        />
      </div>

      <div class="field">
        <label for="contribs">
          Contributors
          <span class="muted">(one email per line, or comma-separated)</span>
        </label>
        <Textarea
          id="contribs"
          v-model="draft.contributors"
          rows="3"
          autoResize
          placeholder="dev1@multiply.cloud&#10;dev2@multiply.cloud"
        />
      </div>

      <div class="row">
        <div class="field">
          <label for="poldoc">Policy document <span class="muted">(optional)</span></label>
          <InputText id="poldoc" v-model="draft.policyDocument" />
        </div>
        <div class="field">
          <label for="polsec">Policy section <span class="muted">(optional)</span></label>
          <InputText id="polsec" v-model="draft.policySection" />
        </div>
      </div>

      <Message v-if="error" severity="error" :closable="false" size="small">
        {{ error }}
      </Message>

      <div class="actions">
        <Button
          label="Cancel"
          severity="secondary"
          :disabled="submitting"
          @click="onVisibleUpdate(false)"
        />
        <Button
          label="Submit event"
          icon="pi pi-send"
          type="submit"
          :loading="submitting"
        />
      </div>
    </form>
  </Drawer>
</template>

<style scoped>
.drawer-header {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.drawer-title { font-weight: 700; font-size: 16px; }
.drawer-sub { font-size: 12px; color: var(--p-surface-500); }
.form { display: flex; flex-direction: column; gap: 14px; }
.row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
}
.field { display: flex; flex-direction: column; gap: 4px; }
.field label {
  font-size: 12px;
  font-weight: 500;
  color: var(--p-surface-700);
}
.muted { color: var(--p-surface-500); font-weight: 400; }
.actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 4px;
}
</style>
